import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import '../assets/ADCreate.css';
import {
  fetchAdLogCodeLists,
  getAdLogCodeList,
  createAdLogCodeList,
  updateAdLogCodeList,
  fetchAdLogCodeCatalog,
  createAdLogCodeCatalogEntry,
} from '../api/restapi';

const EMPTY_FORM = {
  name: '',
  description: '',
  isDefault: false,
  logCodes: [],
};

const EMPTY_ONBOARD = {
  logCode: '',
  hexCode: '',
  name: '',
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    borderColor: state.isFocused ? '#1a56db' : '#d1d5db',
    borderRadius: '6px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26,86,219,.08)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#1a56db' : '#9ca3af' },
  }),
  placeholder: (base) => ({ ...base, fontSize: '13px', color: '#9ca3af' }),
  input: (base) => ({ ...base, fontSize: '13px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    color: state.isSelected ? 'white' : '#1f2937',
    backgroundColor: state.isSelected ? '#1a56db' : state.isFocused ? '#f0f4ff' : 'white',
  }),
  multiValue: (base) => ({ ...base, background: 'var(--gray-100)' }),
  multiValueLabel: (base) => ({ ...base, fontSize: '12px', fontFamily: 'monospace' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function ADLogCodeListManagement({ userInfo }) {
  const formTopRef = useRef(null);

  const [lists, setLists]             = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError]     = useState('');

  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [form, setForm]     = useState(EMPTY_FORM);

  const [bannerMsgs, setBannerMsgs]       = useState([]);
  const [showBanner, setShowBanner]       = useState(false);
  const [bannerSuccess, setBannerSuccess] = useState(false);
  const [saving, setSaving]               = useState(false);

  const [catalog, setCatalog]               = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [showOnboard, setShowOnboard]       = useState(false);
  const [onboard, setOnboard]               = useState(EMPTY_ONBOARD);
  const [onboarding, setOnboarding]         = useState(false);
  const [onboardError, setOnboardError]     = useState('');

  const user = (typeof userInfo === 'string' ? userInfo : userInfo?.mail || userInfo?.email) || 'unknown';

  const [expandedRows, setExpandedRows] = useState(new Set());
  const toggleExpand = (id) => setExpandedRows(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const loadLists = useCallback(async () => {
    setListLoading(true);
    setListError('');
    const data = await fetchAdLogCodeLists();
    if (data && data.errorMessage) {
      setListError(data.errorMessage);
      setLists([]);
    } else {
      setLists(Array.isArray(data) ? data : []);
    }
    setListLoading(false);
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    const data = await fetchAdLogCodeCatalog();
    setCatalog(Array.isArray(data) ? data : []);
    setCatalogLoading(false);
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const showMsg = (msgs, success = false) => {
    setBannerMsgs(Array.isArray(msgs) ? msgs : [msgs]);
    setBannerSuccess(success);
    setShowBanner(true);
    if (success) setTimeout(() => setShowBanner(false), 4000);
  };

  const setF = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const catalogByCode = React.useMemo(() => {
    const m = new Map();
    catalog.forEach(c => m.set(c.logCode, c));
    return m;
  }, [catalog]);

  const codeLabel = (code) => {
    const c = catalogByCode.get(code);
    if (!c) return String(code);
    return [c.name, c.hexCode, code].filter(Boolean).join(' · ');
  };

  const catalogOptions = catalog.map(c => ({
    value: c.logCode,
    label: [c.name, c.hexCode, c.logCode].filter(Boolean).join(' · '),
  }));

  const selectedOptions = form.logCodes.map(code => {
    const opt = catalogOptions.find(o => o.value === code);
    return opt || { value: code, label: String(code) };
  });

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setEditName('');
    setShowBanner(false);
    setShowOnboard(false);
  };

  const openEdit = async (id, name) => {
    setShowBanner(false);
    setShowOnboard(false);
    const data = await getAdLogCodeList(id);
    if (data && data.errorMessage) {
      setListError(`Failed to load log code: ${data.errorMessage}`);
      return;
    }
    setEditId(id);
    setEditName(name || '');
    setForm({
      name:        data.name        || '',
      description: data.description || '',
      isDefault:   !!data.isDefault,
      logCodes:    Array.isArray(data.logCodes) ? data.logCodes : [],
    });
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOnboard = async () => {
    setOnboardError('');
    const code = parseInt(onboard.logCode, 10);
    if (isNaN(code))          { setOnboardError('Log code must be a decimal integer.'); return; }
    if (!onboard.name.trim()) { setOnboardError('Name is required.'); return; }
    setOnboarding(true);
    const body = {
      logCode: code,
      hexCode: onboard.hexCode.trim() || null,
      name:    onboard.name.trim(),
    };
    const result = await createAdLogCodeCatalogEntry(body);
    setOnboarding(false);
    if (result && result.errorMessage) { setOnboardError(result.errorMessage); return; }
    await loadCatalog();
    setForm(prev => prev.logCodes.includes(code)
      ? prev
      : { ...prev, logCodes: [...prev.logCodes, code] });
    setOnboard(EMPTY_ONBOARD);
    setShowOnboard(false);
  };

  const validate = () => {
    const errs = [];
    if (!form.name.trim())          errs.push('Name is required.');
    if (form.logCodes.length === 0) errs.push('At least one log code is required.');
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (errs.length) { showMsg(errs); return; }
    setSaving(true);
    const body = {
      name:        form.name.trim(),
      description: form.description.trim() || null,
      isDefault:   !!form.isDefault,
      logCodes:    form.logCodes,
    };
    const result = editId
      ? await updateAdLogCodeList(editId, body, user)
      : await createAdLogCodeList(body, user);
    setSaving(false);
    if (result && result.errorMessage) { showMsg([result.errorMessage]); return; }
    showMsg([editId ? '✅ Log code updated successfully.' : '✅ Log code created successfully.'], true);
    setTimeout(async () => { resetForm(); await loadLists(); }, 1000);
  };

  return (
    <div className="ad-create-scope">
      <div className="page">

        {/* ── FORM SECTION ── */}
        <div ref={formTopRef}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div className="page-title" style={{ marginBottom: 0 }}>
              {editId ? `Edit Log Code${editName ? ` — ${editName}` : ''}` : 'Create Log Code'}
            </div>
            {editId && (
              <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={resetForm}>
                ✕ Cancel Edit
              </button>
            )}
          </div>
          <div className="page-sub" style={{ marginBottom: 16 }}>
            Define a named set of decimal log packet codes for use in Anomaly Detection rules.
          </div>

          {showBanner && (
            <div className="val-banner" style={bannerSuccess ? {
              background: 'var(--green-light)', borderColor: '#86efac', color: 'var(--green)',
            } : {}}>
              {bannerSuccess ? bannerMsgs[0] : (
                <>
                  <strong>Please resolve the following before continuing:</strong>
                  <ul>{bannerMsgs.map((m, i) => <li key={i}>{m}</li>)}</ul>
                </>
              )}
            </div>
          )}

          {/* Section 1: List Details */}
          <div className="card">
            <div className="card-hdr">
              <div className="step-badge">1</div>
              <h3>List Details</h3>
            </div>
            <div className="card-body">
              <div className="form-grid form-grid-2">
                <div className="field">
                  <label>Name <span className="req">*</span></label>
                  <input type="text" className="inp"
                    placeholder="e.g. Custom AD Log Code"
                    value={form.name}
                    onChange={e => setF('name', e.target.value)} />
                </div>

                <div className="field" style={{ gridColumn: 'span 2' }}>
                  <label>Description</label>
                  <textarea className="ta"
                    placeholder="Optional description of this log code…"
                    value={form.description}
                    onChange={e => setF('description', e.target.value)} />
                </div>

                <div className="field">
                  <label>Default</label>
                  <div className="toggle-wrap" style={{ paddingTop: 8 }}>
                    <label className="toggle">
                      <input type="checkbox" checked={!!form.isDefault}
                        onChange={e => setF('isDefault', e.target.checked)} />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">
                      {form.isDefault ? 'Default mask (pre-selected in AD rules)' : 'Not default'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Log Codes */}
          <div className="card">
            <div className="card-hdr">
              <div className="step-badge">2</div>
              <h3>Log Codes</h3>
              <span className="badge badge-amber" style={{ marginLeft: 'auto' }}>Required</span>
            </div>
            <div className="card-body">
              <div className="trigger-helper" style={{ marginBottom: 14 }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"
                  viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                </svg>
                <span>
                  Select decimal log packet codes from the catalog.{' '}
                  Can't find a code?{' '}
                  <span style={{ color: 'var(--blue)', cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => { setShowOnboard(s => !s); setOnboardError(''); }}>
                    Onboard a new log code ↗
                  </span>
                </span>
              </div>

              <div className="field">
                <label>
                  Log Codes <span className="req">*</span>
                  {form.logCodes.length > 0 && (
                    <span className="badge badge-blue" style={{ marginLeft: 8, fontSize: 10 }}>
                      {form.logCodes.length} selected
                    </span>
                  )}
                </label>
                <Select
                  isMulti
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  options={catalogOptions}
                  value={selectedOptions}
                  isLoading={catalogLoading}
                  placeholder={catalogLoading ? 'Loading catalog…' : 'Search and select log codes…'}
                  noOptionsMessage={() => 'No log codes in catalog'}
                  onChange={(opts) => setF('logCodes', (opts || []).map(o => o.value))}
                />
              </div>

              {showOnboard && (
                <div style={{
                  marginTop: 14, padding: '14px 16px',
                  background: 'var(--gray-50)', border: '1px solid var(--gray-200)',
                  borderRadius: 'var(--radius)',
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-700)', marginBottom: 10 }}>
                    Onboard a new log code
                  </div>
                  {onboardError && (
                    <div className="val-banner" style={{ marginBottom: 10 }}>{onboardError}</div>
                  )}
                  <div className="form-grid form-grid-2" style={{ marginBottom: 10 }}>
                    <div className="field">
                      <label>Log Code (decimal) <span className="req">*</span></label>
                      <input type="text" className="inp" placeholder="e.g. 12345"
                        value={onboard.logCode}
                        onChange={e => setOnboard(o => ({ ...o, logCode: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>Hex Code</label>
                      <input type="text" className="inp" placeholder="e.g. 0x3039"
                        value={onboard.hexCode}
                        onChange={e => setOnboard(o => ({ ...o, hexCode: e.target.value }))} />
                    </div>
                    <div className="field" style={{ gridColumn: 'span 2' }}>
                      <label>Name <span className="req">*</span></label>
                      <input type="text" className="inp" placeholder="e.g. Custom Log Code"
                        value={onboard.name}
                        onChange={e => setOnboard(o => ({ ...o, name: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={{ padding: '5px 14px', fontSize: 12 }}
                      onClick={handleOnboard} disabled={onboarding}>
                      {onboarding ? 'Onboarding…' : 'Onboard & Select'}
                    </button>
                    <button className="btn btn-ghost" style={{ padding: '5px 14px', fontSize: 12 }}
                      onClick={() => { setShowOnboard(false); setOnboard(EMPTY_ONBOARD); setOnboardError(''); }}
                      disabled={onboarding}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Inline save/cancel buttons */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 32 }}>
            <button className="btn btn-ghost" onClick={resetForm} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editId ? 'Update Log Code →' : 'Save Log Code →'}
            </button>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div style={{ borderTop: '2px solid #e2e8f0', marginBottom: 28 }} />

        {/* ── LIST SECTION ── */}
        <div className="page-title" style={{ marginBottom: 4 }}>All Log Codes</div>
        <div className="page-sub" style={{ marginBottom: 14 }}>
          Click Edit to pre-fill the form above.
        </div>

        {listError && <div className="val-banner">{listError}</div>}

        {listLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--gray-400)' }}>
            Loading log codes…
          </div>
        ) : lists.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="empty-handles" style={{ padding: '40px 0' }}>
                No log codes yet. Use the form above to create one.
              </div>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body" style={{ padding: 0 }}>
              <table className="view-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Log Codes Index</th>
                    <th>Log Codes</th>
                    <th>Default</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lists.map(l => (
                    <tr key={l.id} style={editId === l.id ? { background: '#eff6ff' } : {}}>
                      <td style={{ fontWeight: 500 }}>{l.name}</td>
                      <td>{l.logCodesIndex}</td>
                      <td>
                        {Array.isArray(l.logCodes) && l.logCodes.length > 0 ? (() => {
                          const PREVIEW = 3;
                          const expanded = expandedRows.has(l.id);
                          const visible = expanded ? l.logCodes : l.logCodes.slice(0, PREVIEW);
                          const hidden = l.logCodes.length - PREVIEW;
                          return (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                              {visible.map((code, i) => (
                                <span key={i} title={codeLabel(code)} style={{
                                  fontFamily: 'monospace', fontSize: 11,
                                  background: 'var(--gray-100)', border: '1px solid var(--gray-200)',
                                  borderRadius: 3, padding: '1px 5px', color: 'var(--gray-700)',
                                }}>{code}</span>
                              ))}
                              {!expanded && hidden > 0 && (
                                <span onClick={() => toggleExpand(l.id)} style={{
                                  fontSize: 11, cursor: 'pointer', color: 'var(--blue)',
                                  fontWeight: 500, whiteSpace: 'nowrap',
                                }}>+{hidden} more</span>
                              )}
                              {expanded && (
                                <span onClick={() => toggleExpand(l.id)} style={{
                                  fontSize: 11, cursor: 'pointer', color: 'var(--gray-400)',
                                  fontWeight: 500, whiteSpace: 'nowrap',
                                }}>show less</span>
                              )}
                            </div>
                          );
                        })() : '—'}
                      </td>
                      <td>
                        {l.isDefault
                          ? <span className="badge badge-green">Default</span>
                          : <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 12 }}>
                        {l.description || '—'}
                      </td>
                      <td>
                        <button className="btn btn-secondary"
                          style={{ padding: '4px 12px', fontSize: 12 }}
                          onClick={() => openEdit(l.id, l.name)}>
                          {editId === l.id ? 'Editing…' : 'Edit'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ADLogCodeListManagement;
